'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Bell,
  BadgeCheck,
  Check,
  CheckCircle2,
  Coins,
  Download,
  Gift,
  Loader2,
  LogIn,
  ThumbsUp,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { loadNotifications, markAllNotificationsRead, markNotificationRead } from '@/services/account';
import { getNotificationCopy, type NotificationCopy } from '@/utils/notification-copy';
import { useLangDataStore, useNotificationStore, useUserStore } from '@/utils/util_states';
import { ENotificationType, type UserNotification } from '@/utils/util_types';

type NotificationCenterProps = {
  variant?: 'page' | 'drawer';
  active?: boolean;
  onClose?: () => void;
};

function interpolate(template: string, params: Record<string, unknown>) {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(params[key] ?? ''));
}

function notificationContent(notification: UserNotification, copy: NotificationCopy) {
  const reasonMap: Record<string, string> = {
    title: copy.notif_rejected_reason_title,
    soup: copy.notif_rejected_reason_soup,
    truth: copy.notif_rejected_reason_truth,
    author_name: copy.notif_rejected_reason_author_name,
    origin_type: copy.notif_rejected_reason_origin_type,
  };

  switch (notification.type) {
    case ENotificationType.WELCOME:
      return { title: copy.notif_welcome_title, body: copy.notif_welcome_body };
    case ENotificationType.RECHARGE_SUCCESS:
      return { title: copy.notif_recharge_success_title, body: interpolate(copy.notif_recharge_success_body, notification.params) };
    case ENotificationType.SUBSCRIPTION_BENEFIT_GRANTED:
      return { title: copy.notif_subscription_benefit_title, body: interpolate(copy.notif_subscription_benefit_body, notification.params) };
    case ENotificationType.PUZZLE_APPROVED:
      return { title: copy.notif_puzzle_approved_title, body: interpolate(copy.notif_puzzle_approved_body, notification.params) };
    case ENotificationType.PUZZLE_REJECTED:
      return {
        title: copy.notif_puzzle_rejected_title,
        body: interpolate(copy.notif_puzzle_rejected_body, {
          ...notification.params,
          reason: reasonMap[String(notification.params.reason)] ?? notification.params.reason,
        }),
      };
    case ENotificationType.PUZZLE_LIKES_SUMMARY:
      return { title: copy.notif_puzzle_likes_title, body: interpolate(copy.notif_puzzle_likes_body, notification.params) };
    case ENotificationType.NEW_VERSION:
      return { title: copy.notif_new_version_title, body: interpolate(copy.notif_new_version_body, notification.params) };
    default:
      return { title: notification.type, body: Object.values(notification.params).map(String).join(' · ') };
  }
}

function NotificationIcon({ type }: { type: string }) {
  const className = 'h-5 w-5 shrink-0';
  if (type === ENotificationType.WELCOME) return <Gift className={`${className} text-lamp`} />;
  if (type === ENotificationType.RECHARGE_SUCCESS) return <Coins className={`${className} text-[var(--noir-orange)]`} />;
  if (type === ENotificationType.SUBSCRIPTION_BENEFIT_GRANTED) return <BadgeCheck className={`${className} text-lamp`} />;
  if (type === ENotificationType.PUZZLE_APPROVED) return <CheckCircle2 className={`${className} text-green-500`} />;
  if (type === ENotificationType.PUZZLE_REJECTED) return <XCircle className={`${className} text-red-500`} />;
  if (type === ENotificationType.PUZZLE_LIKES_SUMMARY) return <ThumbsUp className={`${className} text-lamp`} />;
  if (type === ENotificationType.NEW_VERSION) return <Download className={`${className} text-lamp`} />;
  return <Bell className={className} />;
}

export default function NotificationCenter({ variant = 'page', active = true, onClose }: NotificationCenterProps) {
  const router = useRouter();
  const { authReady, isLogin } = useUserStore();
  const { langData, languageCode } = useLangDataStore();
  const { setUnreadCount } = useNotificationStore();
  const copy = useMemo(() => getNotificationCopy(languageCode, langData), [langData, languageCode]);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [selected, setSelected] = useState<UserNotification | null>(null);
  const [loading, setLoading] = useState(true);
  const unreadCount = notifications.filter((notification) => !notification.is_read).length;
  const isDrawer = variant === 'drawer';

  useEffect(() => {
    if (!active || !authReady) return;
    if (!isLogin) {
      setNotifications([]);
      setSelected(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void loadNotifications()
      .then((result) => {
        if (cancelled) return;
        setNotifications(result.notifications);
        setUnreadCount(result.unread_count);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Load notifications failed:', error);
          toast.error(langData.profile_network_error);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, authReady, isLogin, langData.profile_network_error, setUnreadCount]);

  async function openNotification(notification: UserNotification) {
    setSelected(notification);
    if (notification.is_read) return;

    try {
      await markNotificationRead(notification.id);
      setNotifications((current) => current.map((item) => (
        item.id === notification.id ? { ...item, is_read: true } : item
      )));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Mark notification read failed:', error);
      toast.error(langData.profile_network_error);
    }
  }

  async function markAllRead() {
    try {
      await markAllNotificationsRead();
      setNotifications((current) => current.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Mark notifications read failed:', error);
      toast.error(langData.profile_network_error);
    }
  }

  function goToLogin() {
    onClose?.();
    router.push('/login');
  }

  function editRejectedPuzzle(puzzleId: string) {
    setSelected(null);
    onClose?.();
    router.push(`/create?edit=${encodeURIComponent(puzzleId)}`);
  }

  return (
    <section className={cn(isDrawer ? 'flex h-full min-h-0 flex-col' : 'mx-auto max-w-4xl px-4 py-8 sm:px-8')}>
      <div className={cn(
        'flex items-center justify-between gap-3',
        isDrawer ? 'min-h-[72px] border-b border-border px-5 py-3' : 'mb-6',
      )}>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lamp">Inbox</p>
          <h1 className={cn('mt-1 truncate font-bold', isDrawer ? 'text-lg' : 'text-2xl')}>{copy.notif_page_title}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size={isDrawer ? 'icon-sm' : 'sm'}
              onClick={() => void markAllRead()}
              aria-label={copy.notif_mark_all_read}
              title={isDrawer ? copy.notif_mark_all_read : undefined}
              className={cn(!isDrawer && 'gap-2')}
            >
              <Check size={14} />
              {!isDrawer && copy.notif_mark_all_read}
            </Button>
          )}
          {isDrawer && onClose && (
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close notifications">
              <X size={18} />
            </Button>
          )}
        </div>
      </div>

      {!authReady || loading ? (
        <div className={cn('flex items-center justify-center', isDrawer ? 'min-h-0 flex-1' : 'py-20')} aria-label="Loading notifications">
          <Loader2 className="animate-spin text-lamp" />
        </div>
      ) : !isLogin ? (
        <div className={cn('flex flex-col items-center justify-center px-6 text-center', isDrawer ? 'min-h-0 flex-1 py-12' : 'min-h-[60vh]')}>
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-2 border-border bg-card">
            <User className="h-10 w-10 text-muted-foreground/40" />
          </div>
          <h2 className="mb-2 text-xl font-semibold" style={{ fontFamily: 'var(--font-playfair)' }}>
            {langData.profile_not_logged_in}
          </h2>
          <p className="mb-8 max-w-xs text-sm leading-relaxed text-muted-foreground/60">
            {langData.profile_not_logged_in_description}
          </p>
          <Button onClick={goToLogin} className="px-7">
            <LogIn size={16} />
            {langData.profile_login_now}
          </Button>
        </div>
      ) : (
        <div className={cn('overflow-hidden border border-border bg-card', isDrawer && 'min-h-0 flex-1 overflow-y-auto border-x-0 border-b-0')}>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-muted-foreground">
              <Bell className="h-8 w-8 opacity-40" />
              <p className="text-sm">{copy.notif_empty}</p>
            </div>
          ) : notifications.map((notification, index) => {
            const content = notificationContent(notification, copy);
            const daysAgo = Math.max(0, Math.floor((Date.now() - new Date(notification.created_at).getTime()) / 86_400_000));
            return (
              <button
                key={notification.id}
                type="button"
                onClick={() => void openNotification(notification)}
                className={cn(
                  'flex w-full items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-accent/40',
                  index > 0 && 'border-t border-border',
                )}
              >
                <NotificationIcon type={notification.type} />
                <span className="min-w-0 flex-1">
                  <strong className="block text-sm">{content.title}</strong>
                  <span className="mt-1 block truncate text-xs text-muted-foreground">{content.body}</span>
                  <span className="mt-2 block text-[10px] text-muted-foreground/60">
                    {new Intl.RelativeTimeFormat(languageCode, { numeric: 'auto' }).format(-daysAgo, 'day')}
                  </span>
                </span>
                {!notification.is_read && <span className="mt-1 h-2 w-2 rounded-full bg-red-500" aria-label="Unread" />}
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-6" onClick={() => setSelected(null)}>
          <div role="dialog" aria-modal="true" className="w-full max-w-lg border border-border bg-background p-6 sm:rounded-sm" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start gap-3">
              <NotificationIcon type={selected.type} />
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold">{notificationContent(selected, copy).title}</h2>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">{notificationContent(selected, copy).body}</p>
                <p className="mt-4 text-xs text-muted-foreground/60">{new Date(selected.created_at).toLocaleString(languageCode)}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} aria-label="Close notification details">
                <X size={18} />
              </button>
            </div>
            {selected.type === ENotificationType.PUZZLE_REJECTED && typeof selected.params.puzzle_id === 'string' && (
              <Button className="mt-6 w-full" onClick={() => editRejectedPuzzle(String(selected.params.puzzle_id))}>
                {copy.notif_puzzle_rejected_edit}
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
